import React, { useEffect, useMemo, useState, useContext } from "react";
import { Grid, Typography, Box, Divider, CircularProgress, Snackbar, Icon } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Adjust, Assistant, BubbleChart, Category } from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import MDEditor from "@uiw/react-md-editor";

// Theme helpers
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";


// ============ Styles ============
const useStyles = makeStyles((theme) => ({
    gridItem: {
        flexBasis: "100%",
        maxWidth: "100%",
        [theme.breakpoints.up("md")]: {
            flexBasis: "25%",
            maxWidth: "25%",
        },
    },
    tile: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        transition: "box-shadow 120ms ease, transform 120ms ease",
        "&:hover": { boxShadow: theme.shadows[5], cursor: "pointer", transform: "translateY(-1px)" },
    },
}));

/**
 * Ideation
 * Material-UI v4 • React 18 compatible
 *
 * Props
 * - caseStudyID: string
 * - caseStudy: object
 * - loading: boolean
 * - setActiveStep(stepIndex: number)
 * - onSave(payload)
 * - onNext(payload)
 */
const Ideation = ({
    caseStudyID,
    caseStudy = {},
    loading = false,
    setActiveStep = () => { },
    onSave = () => { },
    onNext = () => { },
    caseStudyDetails = {},
    setCaseStudyDetails = () => { },
}) => {
    const classes = useStyles();

    // ====== derive initial ideas (exactly 4 tiles at top) ======
    const initialIdeas = useMemo(() => {
        const arr = caseStudy?.ideation?.ideas || [];
        return arr.slice(0, 4).map((it) => ({ name: it.name || "Idea", description: it.description || "" }));
    }, [caseStudy?.ideation?.ideas]);

    const initialIndex = useMemo(() => {
        const idx = caseStudy?.selected_idea_index ?? 0;
        return Math.min(Math.max(idx, 0), Math.max(0, initialIdeas.length - 1));
    }, [caseStudy?.selected_idea_index, initialIdeas.length]);

    const [ideas, setIdeas] = useState(initialIdeas);
    const [selectedIndex, setSelectedIndex] = useState(initialIndex);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: "" });

    useEffect(() => {
        setIdeas(initialIdeas);
        setSelectedIndex(initialIndex);
    }, [initialIdeas, initialIndex]);

    const icons = [Adjust, Assistant, BubbleChart, Category];

    const selected = ideas[selectedIndex] || { name: "", description: "" };

    const onSelect = (idx) => setSelectedIndex(idx);

    const onDescChange = (value) => {
        setIdeas((prev) => prev.map((it, i) => (i === selectedIndex ? { ...it, description: value || "" } : it)));
    };

    const hasRequired = ideas.length > 0 && selected?.name.trim().length > 0;

    const handleSave = async () => {
        const payload = {
            ...caseStudyDetails,
            ideation: { ideas: ideas },
            selected_idea_index: selectedIndex,
        };
        setCaseStudyDetails(payload);

        onSave(payload);
    };

    const handleNext = async () => {
        const payload = {
            ideation: { ideas: ideas },
            selected_idea_index: selectedIndex,
        };
        onNext(payload);
    };

    const handleBack = () => setActiveStep?.(1);

    return (
        <Box>
            <Typography variant="h5">Idea Generation & Selection</Typography>
            <Typography variant="body1" style={{ marginBottom: 10 }}>
            Explore candidate angles and learning objectives, compare options, and refine the strongest idea to align with goals.
            </Typography>
            <Divider style={{ marginBottom: 20 }} />

            {/* Top tiles (exactly 4) */}
            <Grid container spacing={2} justifyContent="center">
                {ideas.map((it, idx) => (
                    <Grid item key={idx} className={classes.gridItem}>
                        <Box
                            className={classes.tile}
                            onClick={() => onSelect(idx)}
                            style={{
                                backgroundColor: idx === selectedIndex ? quCreateColor : "transparent",
                                color: idx === selectedIndex ? "#fff" : quCreateColor,
                            }}
                        >
                            <Box mb={1}>{icons[idx] && React.createElement(icons[idx], { fontSize: "large" })}</Box>
                            <Typography variant="body1">{it.name}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Editor for the selected idea */}
            <Grid container spacing={3} alignItems="flex-start" style={{ marginTop: 20 }}>
                <Grid item xs={12}>
                    <div data-color-mode="light">
                        <MarkdownEditorWithPreview
                            value={selected?.description || ""}
                            onChange={onDescChange}
                            height={350}
                        />
                    </div>
                </Grid>
            </Grid>

            <Grid container style={{ marginTop: 10 }}>
                <Grid item xs={12}>
                    <Button color="quCreateColor" style={{ float: "left", fontWeight: "bold" }} onClick={handleBack}>
                        Back
                    </Button>

                    <Button
                        color="quCreateColor"
                        variant="contained"
                        style={{ float: "right", fontWeight: "bold" }}
                        onClick={handleNext}
                        disabled={loading || saving || !hasRequired}
                    >
                        {loading || saving ? <CircularProgress size={20} /> : "Next"}
                    </Button>
                    <Button
                        color="quCreateColor"
                        style={{ float: "right", fontWeight: "bold", marginLeft: 10, marginRight: 10 }}
                        onClick={handleSave}
                        disabled={loading || saving || !hasRequired}
                    >
                        <Icon>save</Icon>
                        {loading || saving ? <CircularProgress size={20} /> : "Save Changes"}
                    </Button>
                </Grid>
            </Grid>

            <Snackbar
                open={snack.open}
                onClose={() => setSnack({ open: false, message: "" })}
                message={snack.message}
                autoHideDuration={2400}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            />
        </Box>
    );
};

export default Ideation;
